import {
  Component,
  ViewChild,
  ElementRef,
  Renderer2,
  OnDestroy,
  Inject,
  PLATFORM_ID,
  OnInit,
  AfterViewInit,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Store, select } from '@ngrx/store';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { map, filter, distinctUntilChanged, take } from 'rxjs/operators';

import { BookingHeaderSummary } from '@fcom/common/interfaces';
import { ModalButtons, NotificationTheme, PopoverOptions, PopoverPosition } from '@fcom/ui-components';
import { addClass, removeClass, unsubscribe } from '@fcom/core/utils';
import { AppState, ConfigService, StorageService } from '@fcom/core';
import { WindowRef } from '@fcom/core/providers/window';
import { loginStatus } from '@fcom/core/selectors';
import { LoginStatus } from '@fcom/core-api/login';
import { getTripTypeFromBookingLocationCodes } from '@fcom/common-booking/utils/common-booking.utils';
import { BookingLocationCodes } from '@fcom/common/interfaces/booking';
import { GlobalBookingTripDates, MediaQueryService, ToasterService } from '@fcom/common';
import { createPaymentObject, getDatesForJourney, getFlightsAsBookingLocationCodes } from '@fcom/one-upgrade/utils';
import { PaymentType } from '@fcom/one-upgrade/interfaces';
import { Buyer, Cabin, FlightV2, UpgradeProduct, VoucherType } from '@fcom/one-upgrade/api/models';

import { ModalActions } from '../../store/actions';
import {
  getJourneySummary,
  getFlights,
  getBasketPassengerData,
  getPassengers,
  getBasketFlights,
  getUser,
} from '../../store/selectors';

interface Balance {
  [PaymentType.points]: number;
  [PaymentType.EUROPE_UPGRADE]: number;
  [PaymentType.LONG_HAUL_UPGRADE]: number;
  [PaymentType.UPGRADE_BENEFIT]: number;
}

@Component({
  selector: 'fin-upgrade-status-bar',
  templateUrl: './status-bar.component.html',
  styleUrls: ['./status-bar.component.scss'],
})
export class StatusBarComponent implements OnInit, OnDestroy, AfterViewInit {
  readonly SvgLibraryIcon = SvgLibraryIcon;

  static ANIMATION_DURATION = 200;

  bookingSummary$: Observable<BookingHeaderSummary>;

  accordionOpen = false;

  journeySummary$: Observable<BookingLocationCodes[]>;
  basketFlights$: Observable<
    { flight: FlightV2; flightId: string; cabin: Cabin; productName: UpgradeProduct; paymentType: PaymentType }[]
  >;
  loggedIn$: Observable<boolean>;

  availableBenefits$: Observable<Balance>;
  originalBalance$: Observable<Balance>;

  enableUpgradeBenefits = false;
  upgradeBenefitTooltipId = 'upgrade-benefit-warning';
  upgradeBenefitTooltipSeenKey = 'upgrade-benefit-warning-seen';

  tooltipOptions: PopoverOptions = {
    popoverID: 'upgrade-benefits-tooltip',
    popoverPosition: PopoverPosition.BOTTOM,
    popoverWidth: 300,
    openByDefault: false,
    enableOnClick: true,
    showCloseButton: true,
  };

  tooltipOptionsMobile: PopoverOptions = {
    ...this.tooltipOptions,
    zIndex: 4,
  };

  @ViewChild('statusAccordion', { static: false }) statusAccordion: ElementRef;
  @ViewChild('walletOverlay', { static: false }) walletOverlay: ElementRef;
  @ViewChild('toasterContent', { static: false }) toasterContent: ElementRef;

  frameRef: number;
  timeoutRef: ReturnType<typeof setTimeout>;
  subscriptions: Subscription = new Subscription();

  constructor(
    private store$: Store<AppState>,
    private renderer: Renderer2,
    private window: WindowRef,
    private configService: ConfigService,
    private toasterService: ToasterService,
    private mediaQueryService: MediaQueryService,
    private storageService: StorageService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platform: string
  ) {}

  ngOnInit(): void {
    this.journeySummary$ = this.store$.pipe(select(getJourneySummary));
    this.basketFlights$ = this.store$.pipe(select(getBasketFlights));
    this.loggedIn$ = this.store$.pipe(
      loginStatus(),
      map((status: LoginStatus) => status === LoginStatus.LOGGED_IN)
    );
    this.enableUpgradeBenefits = this.configService.cfg.enableUpgradeBenefitInUpgrade;

    this.bookingSummary$ = combineLatest([
      this.store$.pipe(select(getFlights)),
      this.store$.pipe(select(getBasketPassengerData)),
      this.store$.pipe(select(getPassengers)),
    ]).pipe(
      filter(([flights]) => Boolean(flights.length)),
      map(([flights, selectedPax, pax]) => {
        const bookingLocations = getFlightsAsBookingLocationCodes(flights);
        const tripType = getTripTypeFromBookingLocationCodes(bookingLocations);
        const tripDates: GlobalBookingTripDates = getDatesForJourney(flights, tripType);
        const paxAmount = selectedPax.length ? selectedPax.length : pax.length;

        return {
          tripType: tripType,
          locations: bookingLocations,
          paxAmount: paxAmount,
          dates: tripDates,
        };
      })
    );

    this.originalBalance$ = this.store$.pipe(
      select(getUser),
      map((buyer: Buyer) => {
        return {
          [PaymentType.EUROPE_UPGRADE]:
            buyer?.vouchers?.find((voucher) => voucher.type === VoucherType.EuropeUpgrade)?.count || 0,
          [PaymentType.LONG_HAUL_UPGRADE]:
            buyer?.vouchers?.find((voucher) => voucher.type === VoucherType.LongHaulUpgrade)?.count || 0,
          [PaymentType.UPGRADE_BENEFIT]:
            buyer?.vouchers?.find((voucher) => voucher.type === VoucherType.UpgradeBenefit)?.count || 0,
          [PaymentType.points]: buyer?.points || 0,
        };
      })
    );

    this.availableBenefits$ = this.getRemainingBalance();

    if (!this.storageService.LOCAL.get(this.upgradeBenefitTooltipSeenKey) && !isPlatformServer(this.platform)) {
      this.tooltipOptions.openByDefault = true;
    }
  }

  ngAfterViewInit(): void {
    if (this.storageService.LOCAL.get(this.upgradeBenefitTooltipSeenKey) || isPlatformServer(this.platform)) {
      return;
    }

    this.subscriptions.add(
      combineLatest([this.originalBalance$, this.mediaQueryService.isBreakpoint$('tablet_down')])
        .pipe(take(1))
        .subscribe(([originalBalance, isTabletDown]) => {
          if (!originalBalance.UPGRADE_BENEFIT) {
            return;
          }

          this.storageService.LOCAL.set(this.upgradeBenefitTooltipSeenKey, true);

          if (!isTabletDown) {
            return;
          }

          setTimeout(() => {
            this.toasterService.addMessageToQueue({
              id: this.upgradeBenefitTooltipId,
              contentHTML: this.toasterContent.nativeElement.innerHTML,
              theme: NotificationTheme.INFO,
              keepMessageAfterNavigation: true,
            });
          }, 0);
        })
    );
  }

  getRemainingBalance(): Observable<Balance> {
    return combineLatest([this.basketFlights$, this.originalBalance$, this.bookingSummary$]).pipe(
      map(([flightsInBasket, originalBalance, bookingSummary]) => {
        const remainingBalance = {
          ...originalBalance,
        };

        const paymentOptions = flightsInBasket.map((flightBasket) =>
          createPaymentObject(flightsInBasket, flightBasket)
        );

        paymentOptions.forEach((paymentOption) => {
          const paymentType = Object.keys(paymentOption)[0] as PaymentType;
          const amountToDeduct = paymentOption[paymentType].price;

          remainingBalance[paymentType] -= amountToDeduct * bookingSummary.paxAmount;
        });

        return remainingBalance;
      }),
      distinctUntilChanged()
    );
  }

  ngOnDestroy(): void {
    clearTimeout(this.timeoutRef);

    this.subscriptions = unsubscribe(this.subscriptions);
    this.toasterService.removeMessage(this.upgradeBenefitTooltipId);

    if (isPlatformBrowser(this.platform)) {
      this.window.nativeWindow.cancelAnimationFrame(this.frameRef);
    }
  }

  toggleWallet(): void {
    this.accordionOpen = !this.accordionOpen;

    if (this.accordionOpen) {
      this.slideAccordionIn();
      this.showOverlay();
    } else {
      this.slideAccordionOut();
      this.hideOverlay();
    }
  }

  openHelpModal(event: Event): void {
    // Stops the modal from closing immediately due to document click events.
    event.stopPropagation();
    this.store$.dispatch(
      ModalActions.openModal({
        modal: {
          title: 'oneUpgrade.helpModal.title',
          fragment: 'fragments.oneUpgrade.help.url',
          buttons: ModalButtons.DONE,
        },
        returnTarget: '[data-id="help-modal-button"]',
      })
    );
  }

  private slideAccordionIn(): void {
    this.showAcc();
    const targetHeight = this.getAccHeight();

    this.frameRef = this.window.nativeWindow.requestAnimationFrame(() => {
      this.setAccHeight('0');

      this.frameRef = this.window.nativeWindow.requestAnimationFrame(() => {
        this.setAccHeight(`${targetHeight}px`);

        // set height to auto after animation so doesn't break on resize
        this.timeoutRef = setTimeout(() => this.setAccHeight('auto'), StatusBarComponent.ANIMATION_DURATION);
      });
    });
  }

  private slideAccordionOut(): void {
    const targetHeight = this.getAccHeight();

    this.frameRef = this.window.nativeWindow.requestAnimationFrame(() => {
      this.setAccHeight(`${targetHeight}px`);

      this.frameRef = this.window.nativeWindow.requestAnimationFrame(() => {
        this.setAccHeight('0');

        this.timeoutRef = setTimeout(() => this.hideAcc(), StatusBarComponent.ANIMATION_DURATION);
      });
    });
  }

  private showOverlay(): void {
    addClass(this.document.body, 'modal-open-laptop-down');
    this.renderer.setStyle(this.walletOverlay.nativeElement, 'opacity', '100');
    this.renderer.setStyle(this.walletOverlay.nativeElement, 'display', 'block');
  }

  private hideOverlay(): void {
    removeClass(this.document.body, 'modal-open-laptop-down');
    this.renderer.setStyle(this.walletOverlay.nativeElement, 'opacity', '0');
    this.renderer.setStyle(this.walletOverlay.nativeElement, 'display', 'none');
  }

  private getAccHeight(): string {
    return this.statusAccordion.nativeElement.offsetHeight;
  }

  private hideAcc(): void {
    this.setAccStyle('display', 'none');
  }

  private showAcc(): void {
    this.setAccHeight('auto');
    this.setAccStyle('display', 'block');
  }

  private setAccHeight(val: string): void {
    this.setAccStyle('height', val);
  }

  private setAccStyle(prop: string, val: string): void {
    this.renderer.setStyle(this.statusAccordion.nativeElement, prop, val);
  }
}
